import Joi from 'joi';
import _ from 'underscore';

import {
	api as apiRequest,
	batchApi as batchApiRequest
} from '@culture/common-helpers/request';
import { instituteRedirect } from '@culture/common-helpers/redirect';
import {
	addCleanCache,
	parsePathParams,
	preparePathParams,
	getBreadcrumbsParams,
	getMetaParams
} from '@culture/common-helpers/params';
import { statusesHash } from '@culture/utils/helpers/statuses';
import getConfig from 'next/config';
import { getBreadcrumbs, getCatalogMeta, getRubrics } from 'logic/page';
import Template from 'pageTemplates/archive';

const {
	serverRuntimeConfig: { entities }
} = getConfig();

const BroadcastArchivePage = (props) => <Template {...props} />;

const validationSchema = Joi.object().keys({
	pathParams: Joi.array().items(Joi.string()),
	cleanCache: Joi.boolean(),
	page: Joi.number().default(1),
	limit: Joi.number().default(24),
	sort: Joi.string().default('-views'),
	query: Joi.string()
});

const pathParamsValidationSchema = Joi.object().keys({
	institute: Joi.string(),
	year: Joi.number(),
	type: Joi.string()
});

const getBroadcasts = (params, pathParams) => {
	let broadcastParams = addCleanCache(
		{
			fields: [
				'title',
				'broadcastType',
				'thumbnailFile',
				'institute',
				'name',
				'fromDate',
				'toDate',
				'publishDate',
				'type'
			].join(),
			expand: ['institute'],
			cached: true,
			statuses: [statusesHash.published],
			isPassed: true,
			withVideo: true,
			isWasBroadcast: true,
			excludeTags: [entities?.nationalProjectCultureTagId],
			..._(params).omit('cleanCache', 'pathParams')
		},
		params.cleanCache
	);

	broadcastParams = preparePathParams(broadcastParams, pathParams);

	// `institutes` is reserved for ids so we use `institute` param instead
	if (broadcastParams.institutes) {
		broadcastParams = {
			..._(broadcastParams).omit('institutes'),
			institute: broadcastParams.institutes[0]
		};
	}

	return apiRequest('broadcast', broadcastParams);
};

const getBroadcastsCount = (params) =>
	apiRequest(
		'broadcast/count',
		addCleanCache({ cached: true }, params.cleanCache)
	);

export async function getServerSideProps({ query, resolvedUrl, res }) {
	const params = Joi.attempt(query, validationSchema, {
		stripUnknown: true
	});

	const parsedPathParams = parsePathParams(params.pathParams);

	const pathParams = Joi.attempt(
		parsedPathParams.params,
		pathParamsValidationSchema
	);

	const { url, status } = await instituteRedirect(
		resolvedUrl,
		pathParams.institute
	);

	if (url) {
		res.redirect(status, url);
		return {};
	}

	const rubricPath = 'live/archive';

	const [
		breadcrumbs,
		catalogMeta,
		broadcasts,
		broadcastCount,
		rubrics
	] = await batchApiRequest([
		getBreadcrumbs(getBreadcrumbsParams(rubricPath, params)),
		getCatalogMeta(getMetaParams(rubricPath, params)),
		getBroadcasts(params, pathParams),
		getBroadcastsCount(params),
		getRubrics(rubricPath)
	]);

	return {
		props: {
			breadcrumbs,
			broadcasts: broadcasts.items,
			total: broadcasts.total,
			limit: params.limit,
			catalogMeta,
			rubricPath,
			broadcastCount: broadcastCount.item,
			rubric: rubrics.items.length ? rubrics.items[0] : null,
			urlParams: {
				...pathParams,
				..._(params).omit('pathParams')
			}
		}
	};
}

export default BroadcastArchivePage;
